import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import type { ProspectSource } from './prospects.schemas';

// Quelle values an inquiry can carry; a subset of the Interessent's Quelle (IMM-1098). IMM-1066 adds 'IMMOSCOUT24'.
export const INQUIRY_SOURCES = ['WEBSITE', 'WEB_EXPOSE'] as const satisfies ReadonlyArray<ProspectSource>;
export const InquirySourceSchema = z.enum(INQUIRY_SOURCES);
export type InquirySource = z.infer<typeof InquirySourceSchema>;

export const INQUIRY_OUTCOMES = [
  'PROSPECT_CREATED',
  'ATTACHED_TO_ACTIVE_PROSPECT',
  'NO_PROSPECT_PROPERTY_ARCHIVED',
  'NO_PROSPECT_RENTAL',
] as const;
export const InquiryOutcomeSchema = z.enum(INQUIRY_OUTCOMES);
export type InquiryOutcome = z.infer<typeof InquiryOutcomeSchema>;

export const INQUIRY_REQUIRED_FIELDS = ['name', 'email', 'phone'] as const;
export type InquiryRequiredField = (typeof INQUIRY_REQUIRED_FIELDS)[number];
export const INQUIRY_MESSAGE_MAX_LENGTH = 10_000;

// Every field optional and nullable on purpose: the handler names EVERY missing required field in one 422 (AC 2),
// which the boundary's generic 400 cannot. The maxima only bound abuse; a real form never reaches them.
export const SubmitWebsiteInquiryBodySchema = z.object({
  propertyId: z.string().max(64).nullable().optional(),
  name: z.string().max(300).nullable().optional(),
  email: z.string().max(320).nullable().optional(),
  phone: z.string().max(100).nullable().optional(),
  // No maximum: an over-long message is cut, never refused (AC 5). Fastify's body limit bounds the request.
  message: z.string().nullable().optional(),
  // The hidden check field. A person never fills it in; a non-blank value drops the submission silently (AC 6).
  homepage: z.string().max(2000).nullable().optional(),
});
export type SubmitWebsiteInquiryBody = z.infer<typeof SubmitWebsiteInquiryBodySchema>;

// Read shape: loose on purpose (BL-039). A future source may deliver no phone, and name/e-mail are whatever was kept.
export const PropertyInquirySchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  receivedAt: z.iso.datetime(),
  source: InquirySourceSchema,
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  message: z.string().nullable(),
  outcome: InquiryOutcomeSchema,
  contactId: refersTo('contact', z.uuid()),
  prospectId: refersTo('prospect', z.uuid()).nullable(),
});
export type PropertyInquiry = z.infer<typeof PropertyInquirySchema>;

export const ListPropertyInquiriesResponseSchema = z.object({ data: z.array(PropertyInquirySchema) });
export type ListPropertyInquiriesResponse = z.infer<typeof ListPropertyInquiriesResponseSchema>;
